/**
 * The `entrance` namespace, in English — sign-in, the token landing pages and invite
 * acceptance. One file per (language, namespace); English is the only complete one today.
 *
 * ICU MessageFormat. Placeholders are named, never positional, so a translator can reorder
 * a sentence without reordering the arguments. Where a message varies by a wire value —
 * a project role, an OIDC provider — it varies through an ICU `select` rather than through
 * four keys the component picks between: the branching is then the translator's to reshape,
 * and a role we have never seen still renders a sentence via `other`.
 *
 * TERMINOLOGY (chat1.md §6): the thing we email is a **Sign-in link**. The backend's own
 * name for the mechanism — the one in `POST /api/v1/auth/magic-link-sign-in` — is internal
 * and must never reach a user; `scripts/guard.mjs` fails the build if it does.
 *
 * LENGTH: `scripts/check-expansion.mjs` pseudo-expands every message here by 1.3× and fails
 * the build when one no longer fits the slot it is rendered in (KLQ-20260819-001, phase 3
 * "German +30% text expansion"). A message that trips it is shortened here rather than
 * given more room there.
 */

export const entranceEn = {
  // TWIN: `brand.wordmark` in `./setup.ts`. The wordmark is deliberately duplicated across
  // namespaces so a language pack can ship the entrance without the setup wizard; change it
  // here and it changes there too (retro rule 12).
  'brand.wordmark': 'KALQA',
  'brand.tagline': 'The drawing everyone is holding should be the same drawing.',

  'signIn.title': 'Sign in',

  'signIn.field.email.label': 'Email',
  'signIn.field.email.placeholder': 'you@practice.example',
  'signIn.field.password.label': 'Password',
  'signIn.field.password.show': 'Show password',
  'signIn.field.password.hide': 'Hide password',

  'signIn.action.microsoft': 'Continue with Microsoft',
  'signIn.action.google': 'Continue with Google',
  'signIn.action.emailLink': 'Send me a sign-in link',
  'signIn.action.password': 'Sign in with password',
  'signIn.action.submit': 'Sign in',
  'signIn.action.back': 'Back',
  'signIn.action.signUp': 'No account? Create one',
  'signIn.action.forgot': 'Forgot your password?',

  'signIn.divider': 'or',
  'signIn.linkSent.title': 'Check your inbox',
  'signIn.linkSent.body':
    'We sent a sign-in link to {email}. It works once, and expires in {minutes, plural, one {# minute} other {# minutes}}.',
  'signIn.linkSent.action': 'Use a different address',

  'signIn.error.emailRequired': 'Enter the email address you were invited with.',
  'signIn.error.emailInvalid': 'That does not look like an email address.',
  'signIn.error.passwordRequired': 'Enter your password.',
  'signIn.error.credentials': 'That email and password do not match an account.',
  'signIn.error.ssoDenied': 'Your IT admin needs to grant access to Kalqa before you can sign in.',
  'signIn.error.ssoUnavailable': 'That sign-in provider is not available right now. Try email instead.',
  'signIn.error.inviteRequired': 'Kalqa is invitation-only. Ask whoever runs the project to invite you.',
  'signIn.error.rateLimited': 'Too many attempts. Wait a minute and try again.',
  'signIn.error.botCheck': 'We could not tell you apart from a bot. Reload the page and try again.',
  'signIn.error.unexpected': 'Something went wrong on our side. Try again in a moment.',

  'signIn.status.redirecting': 'Taking you to your provider…',

  // What another screen finished doing, said where the result of it is useful. The reset
  // endpoint issues no session on purpose, so "now sign in" is the honest next sentence and
  // not a consolation.
  'signIn.notice.passwordReset': 'Your new password is saved. Sign in with it now.',

  // --- Sign-up (`/sign-up`) ------------------------------------------------
  // ACCOUNT-EXISTENCE UNIFORMITY: `POST /auth/sign-up` answers `202` with the same body
  // whether or not the address already has an account (`KLQ-20260814-004` §G). Every string
  // below has to hold in both branches — "we sent a message" does, "we created your account"
  // would not. A sentence here that only fits the fresh-address case reintroduces on the
  // screen the oracle the endpoint spent its design refusing to be.
  'signUp.title': 'Create your account',
  'signUp.subtitle': 'Set up Kalqa for your practice. Invite the rest of the team once you are in.',

  'signUp.field.email.label': 'Work email',
  'signUp.field.email.placeholder': 'you@practice.example',
  'signUp.field.password.label': 'Choose a password',
  'signUp.field.password.hint': 'At least 8 characters.',
  'signUp.field.password.show': 'Show password',
  'signUp.field.password.hide': 'Hide password',

  'signUp.action.submit': 'Create account',
  'signUp.action.signIn': 'Have an account? Sign in',

  'signUp.done.title': 'Check your inbox',
  'signUp.done.body':
    'We sent a message to {email}. Open it to confirm the address, then sign in with the password you just chose.',
  'signUp.done.action': 'Use a different address',

  'signUp.error.emailRequired': 'Enter the email address you work from.',
  'signUp.error.emailInvalid': 'That does not look like an email address.',
  'signUp.error.passwordRequired': 'Choose a password.',
  'signUp.error.passwordShort': 'Use at least {min} characters.',
  'signUp.error.passwordLong': 'Use {max} characters or fewer.',
  'signUp.error.legalRequired': 'We cannot create an account without your agreement.',
  'signUp.legal.prefix': 'I agree to the',
  'signUp.legal.terms': 'Terms of Service',
  'signUp.legal.and': 'and the',
  'signUp.legal.privacy': 'Privacy Policy',
  'signUp.legal.suffix': '.',

  // --- Forgotten password (`/auth/forgot-password`) ------------------------
  // ACCOUNT-EXISTENCE UNIFORMITY, again: `POST /auth/password-resets` answers `202` with the
  // same status, body and headers for an address that has an account, one that does not, and
  // one whose account is deactivated (`KLQ-20260821-002` §B). Every string below has to hold
  // in all three, which is why the confirmation says *if*. A sentence that only fits the
  // known-address case puts the oracle back on the screen.
  'forgot.title': 'Forgot your password?',
  'forgot.subtitle':
    'Give us the address you sign in with and we will email you a link to choose a new one.',
  'forgot.field.email.label': 'Email',
  'forgot.field.email.placeholder': 'you@practice.example',
  'forgot.action.submit': 'Email me a reset link',
  'forgot.action.back': 'Back to sign in',
  'forgot.done.title': 'Check your inbox',
  'forgot.done.body':
    'If {email} has an account, a link to choose a new password is on its way. It works once, and expires in {minutes, plural, one {# minute} other {# minutes}}.',
  'forgot.done.action': 'Use a different address',
  'forgot.error.emailRequired': 'Enter the address you sign in with.',
  'forgot.error.emailInvalid': 'That does not look like an email address.',

  // --- Reset landing (`/auth/reset-password`) ------------------------------
  // A button, not an automatic POST, for the same reason as the sign-in link: appliances that
  // scan links open what they are sent, and this token buys one write of the password.
  'reset.title': 'Choose a new password',
  'reset.subtitle': 'Pick a password for your account. You sign in with it on the next screen.',
  'reset.field.new.label': 'New password',
  'reset.field.new.hint': 'At least {min} characters. Length is the only rule.',
  'reset.field.confirm.label': 'New password again',
  'reset.field.show': 'Show password',
  'reset.field.hide': 'Hide password',
  'reset.action.submit': 'Set new password',
  'reset.error.required': 'Choose a password.',
  'reset.error.tooShort': 'Use at least {min} characters.',
  'reset.error.tooLong': 'Use {max} characters or fewer.',
  'reset.error.mismatch': 'The two passwords do not match.',
  'reset.missing.title': 'This link is incomplete',
  'reset.missing.body': 'Open the link from your email again, or ask for a new one.',
  'reset.error.title': 'We could not set that password',
  // One sentence for expired, superseded and already used, because the server answers all
  // three identically and the screen must not invent a distinction it cannot see.
  'reset.error.expired':
    'That link no longer works. Reset links work once, and expire an hour after they are sent.',
  'reset.error.rejected': 'That password was refused. Use at least {min} characters.',
  'reset.error.action': 'Ask for a new link',

  /** The way back to A1 from every landing page. One key, because it is one sentence. */
  'nav.signIn': 'Go to sign in',

  // --- Sign-in-link landing (`/auth/link`) ---------------------------------
  // A button, not an automatic POST: the backend mails a link to a page "with a sign-in
  // button that POSTs" precisely because link-scanning appliances open what they are sent.
  'link.title': 'Finish signing in',
  'link.body': 'Confirm below to sign in on this device.',
  'link.action': 'Sign in',
  'link.missing.title': 'This link is incomplete',
  'link.missing.body': 'Open the sign-in link from your email again, or ask for a new one.',
  'link.error.title': 'This sign-in link no longer works',
  'link.error.body': 'Sign-in links work once and expire quickly. Ask for a new one.',
  'link.error.action': 'Request a new sign-in link',

  // --- Verify-email landing (`/auth/verify-email`) -------------------------
  // The link confirms the address *and* signs in, once (`KLQ-20261023-009` §5).
  'verify.title': 'Confirm your email address',
  'verify.body': 'Confirm below that this address is yours, and you are signed in on this device.',
  'verify.action': 'Confirm and sign in',
  'verify.error.title': 'We could not confirm that address',
  'verify.error.body': 'The link was already used, or it expired. Ask for a new one.',

  // --- OIDC callback (`/auth/oidc/callback`) -------------------------------
  'oidc.provider': '{provider, select, google {Google} microsoft {Microsoft} other {your provider}}',
  'oidc.pending.title': 'Finishing sign-in',
  'oidc.pending.body': 'One moment. We are checking with your provider.',
  'oidc.missing.title': 'Nothing to complete here',
  'oidc.missing.body': 'This page is opened by your sign-in provider. Start from the sign-in screen.',
  'oidc.error.title': 'That sign-in did not complete',
  'oidc.error.body': 'The attempt expired or was already used. Start again from the sign-in screen.',

  // The nOAuth answer: the provider asserted an address it did not prove, so nothing was
  // created, nothing was linked, and the only way on is a message to that mailbox.
  'oidc.confirm.title': 'Confirm it is your address',
  'oidc.confirm.body':
    'We emailed the address {provider} gave us. Open that message to allow {provider} sign-in. Nothing has been created or changed yet.',

  // --- Provider-link confirmation (`/auth/confirm-link`) -------------------
  'confirmLink.title': 'Allow this sign-in provider',
  'confirmLink.body': 'Confirm below that this account may sign in with the linked provider.',
  'confirmLink.action': 'Confirm',
  'confirmLink.done.title': '{provider} is linked',
  'confirmLink.done.body': 'Go back to sign in and choose {provider}.',
  'confirmLink.error.title': 'We could not confirm that link',
  'confirmLink.error.body': 'The link was already used, or it expired. Start the sign-in again.',

  // --- Invite acceptance (`/invitations/accept`, A2) -----------------------
  'invite.title': 'You have been invited',
  'invite.lede': '{workspace} invited you to a project on Kalqa.',
  // The hiring half (`KLQ-20260819-006`). A separate lede rather than a parameterised one:
  // a screen that promised a drawing to somebody being offered a job would be one token
  // serving two purposes with nobody having said so.
  'invite.lede.workspace': '{workspace} invited you to join their team on Kalqa.',
  'invite.label.project': 'Project',
  'invite.label.team': 'Team',
  'invite.role.workspace':
    '{role, select, admin {Admin} manager {Manager} member {Member} other {Team member}}',
  'invite.role.workspaceDetail':
    '{role, select, admin {Runs the team: settings, people, billing. Sees every project.} manager {Sees every project the team owns, and can put people on them.} member {Sees only the projects you are put on. Everything else stays out of sight.} other {Takes part in the team.}}',
  'invite.target.team':
    'Accepting creates your account inside {workspace}. You will not be setting up a company of your own.',
  'invite.done.bodyWorkspace':
    'You are now part of {workspace}. Sign in and we will email you a sign-in link.',
  'invite.label.role': 'Your role',
  'invite.label.invitedAs': 'Invited as',
  'invite.role.name':
    '{role, select, architect {Architect} engineer {Engineer} contractor {Contractor} client_readonly {Client, read only} other {Project member}}',
  'invite.role.detail':
    '{role, select, architect {Leads the project: publishes drawings, runs reviews, manages who is on it.} engineer {Works on the project: publishes drawings, comments and takes on tasks.} contractor {Builds from the drawings: downloads the current set, comments, reports issues.} client_readonly {Sees and comments on everything published, and can download it. Changes nothing.} other {Takes part in the project.}}',
  'invite.target.existing': 'You will join {workspace}, which is already on Kalqa.',
  'invite.target.new': 'We will set up your own studio as part of accepting.',
  'invite.action.accept': 'Accept invitation',
  'invite.action.notMe': 'This is not me',
  'invite.notMe.title': 'Nothing accepted',
  'invite.notMe.body': 'Tell whoever sent it that it reached the wrong person.',
  'invite.error.title': 'This invitation is no longer valid',
  'invite.error.body': 'It may have expired, been used, or been withdrawn. Ask whoever invited you for a new one.',
  'invite.done.title': 'You are in',
  'invite.done.body': 'You now have access to {project}. Sign in and we will email you a sign-in link.',
  'invite.status.loading': 'Reading your invitation…',

  'language.label': 'Language',
  'footer.imprint': 'Legal notice',
  'footer.terms': 'Terms',
  'footer.privacy': 'Privacy',
  'footer.help': 'Help',
  'footer.copyright': '© {year} KALQA',
} as const;

export type EntranceMessageKey = keyof typeof entranceEn;
